import { EntityBase } from '@provision/entity-types';
import { useMemo } from 'react';
import { DynamicIcon } from 'src/components';
import { CustomAttributeColumnRenderer } from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge';
import { AttributeValue } from 'src/pages/CustomViews/custom-views-entities-table/attribute-value';
import {
  CustomComponentsByAttribute,
  getAttributeRawValue,
} from 'src/pages/CustomViews/custom-views-entities-table/render-attribute-value';
import { CardChipLine, ChipLineItem } from './card-chip-line';
import { CardColumns, CardGlyphDisplay, CardLayoutRoles } from './mobile-card-layout';
import {
  CardBody,
  CardTime,
  CardTitle,
  CardTitleRow,
  CardTitleText,
} from './mobile-card-list.elements';
import { useCardChipValueItems } from './use-card-chip-value-items';

const GLYPH_ICON_SIZE_PX = 13;
const GLYPH_CHIP_KEY = 'glyph';

const CardGlyph = ({ display }: { display: CardGlyphDisplay }) => (
  <DynamicIcon
    name={display.icon}
    library={display.library}
    size={GLYPH_ICON_SIZE_PX}
    sx={{ color: display.color }}
  />
);

export const ChipLineCardBody = <T extends EntityBase>({
  record,
  layout,
  cardColumns,
  customAttributeRenderers,
  customComponentsByAttribute,
}: {
  record: T;
  layout: CardLayoutRoles;
  cardColumns: CardColumns;
  customAttributeRenderers?: Map<string, CustomAttributeColumnRenderer>;
  customComponentsByAttribute?: CustomComponentsByAttribute;
}) => {
  const { title, time, glyph } = cardColumns;

  const valueItems = useCardChipValueItems({
    record,
    chips: cardColumns.chips,
    layout,
    customAttributeRenderers,
    customComponentsByAttribute,
  });

  const glyphRawValue = glyph ? getAttributeRawValue(record, glyph) : undefined;
  const glyphDisplay = useMemo(
    () => layout.glyph?.getDisplay(glyphRawValue) ?? null,
    [layout.glyph, glyphRawValue],
  );

  // With the title off the glyph has no title to sit before, so it leads the chip line instead.
  const chipItems = useMemo<ChipLineItem[]>(
    () =>
      glyphDisplay && !title
        ? [
            {
              key: GLYPH_CHIP_KEY,
              node: <CardGlyph display={glyphDisplay} />,
              isPinned: true,
            },
            ...valueItems,
          ]
        : valueItems,
    [glyphDisplay, title, valueItems],
  );

  const timeNode = time ? (
    <CardTime>
      <AttributeValue
        record={record}
        column={time}
        customComponentsByAttribute={customComponentsByAttribute}
      />
    </CardTime>
  ) : null;

  if (!title) {
    return (
      <CardBody>
        {chipItems.length || timeNode ? (
          <CardTitleRow>
            {chipItems.length ? <CardChipLine items={chipItems} isFirstLine /> : null}
            {timeNode}
          </CardTitleRow>
        ) : null}
      </CardBody>
    );
  }

  return (
    <CardBody>
      <CardTitleRow>
        <CardTitle>
          {glyphDisplay ? <CardGlyph display={glyphDisplay} /> : null}
          <CardTitleText>
            <AttributeValue
              record={record}
              column={title}
              customComponentsByAttribute={customComponentsByAttribute}
            />
          </CardTitleText>
        </CardTitle>
        {timeNode}
      </CardTitleRow>
      {chipItems.length ? <CardChipLine items={chipItems} /> : null}
    </CardBody>
  );
};
